import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { Warning, CheckCircle, Clear } from '@material-ui/icons'
import axios from 'axios'
import html2canvas from 'html2canvas'
import { useS3Upload, useConfig } from 'hooks/useConfig'
import LetterBOL from './LetterBOL'

const BolPreviewForRetry = ({ bolData, invInter }) => {
  const date = new Date().toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })

  const bolQR = {
    site: bolData.terminal,
    date: date,
    carrier: bolData.carrier,
    truck: bolData.truck_number,
    driver: bolData.driver_name,
    sandType: bolData.sand_type,
    po: bolData.purchase_order_number
  }

  const total = invInter.reduce((sum, inv, idx) => {
    const weight =
      idx === 0 ? inv.gross_weight - bolData.tare_weight : inv.gross_weight - invInter[idx - 1].gross_weight
    return sum + weight
  }, 0)

  const componentRef = React.useRef()

  return (
    <div
      id="bol-preview-retry"
      style={{
        backgroundColor: 'white',
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
        transform: 'scale(0.5)',
        transformOrigin: 'top left',
        width: '816px'
      }}
    >
      <LetterBOL
        componentRef={componentRef}
        date={date}
        bolQR={bolQR}
        total={total}
        invInters={invInter}
        bolData={{
          bolId: bolData.bolId,
          tare_weight: bolData.tare_weight,
          processed_at: bolData.processed_at,
          terminal: bolData.terminal,
          carrier: bolData.carrier,
          driver_name: bolData.driver_name,
          trailer_number: bolData.trailer_number,
          truck_number: bolData.truck_number,
          status: 'Completed',
          weighmaster_license: bolData.weighmaster_license || '',
          buyer: bolData.buyer || 'Chesapeake Operating L.L.C.',
          purchase_order_number: bolData.purchase_order_number,
          sand_type: bolData.sand_type,
          job_name: bolData.job_name || 'LIONETTI BRA PAD',
          destination_address: bolData.destination_address,
          truck_load_id: bolData.truck_load_id
        }}
      />
    </div>
  )
}

const BOLSyncRetryModal = ({
  open,
  handleClose,
  bolData,
  invInterData,
  getBOLs,
  sqlParams,
  currentTerminal,
  dbUserId
}) => {
  const [isRetrying, setIsRetrying] = useState(false)
  const [retryStatus, setRetryStatus] = useState(null)
  const [retryError, setRetryError] = useState(null)
  const { config } = useConfig()
  const { uploadToS3 } = useS3Upload()

  const updateBolSyncStatus = async (bolId, status, error = null, attempts = 0) => {
    try {
      await axios.post(`${config.LOGITRAC_API_URL}api/update`, {
        array: [
          {
            bolId,
            sanddrive_sync_status: status,
            sanddrive_sync_error: error,
            sanddrive_sync_attempts: attempts
          }
        ],
        dbTable: 'Bills_Of_Lading',
        dbEvent: 'update',
        dbUnique: 'bolId',
        dbUserId
      })
    } catch (error) {
      console.error('Failed to update BOL sync status:', error)
    }
  }

  const handleRetry = async () => {
    setIsRetrying(true)
    setRetryStatus(null)
    setRetryError(null)

    try {
      // Capture BOL image
      const bolElement = document.getElementById('bol-preview-retry')
      if (!bolElement) {
        throw new Error('BOL preview element not found')
      }

      const canvas = await html2canvas(bolElement, {
        scale: 2, // Account for the transform scale
        width: 816,
        height: 1056
      })
      const imageBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.95))

      // Upload to S3
      const file = new File([imageBlob], `bol-${bolData.bolId}.jpg`, { type: 'image/jpeg' })
      const s3Url = await uploadToS3(file)

      // Send to SandDrive
      const sentData = {
        source_fulfillment_id: bolData.source_fulfillment_id,
        bol: String(bolData.bolId),
        gross_weight: bolData.gross_weight,
        measured_weight: bolData.gross_weight - bolData.tare_weight,
        og_permit: false,
        bol_loaded_url: s3Url
      }

      await axios.post(`${config.LOGITRAC_API_URL}api/proxy/sanddrive`, sentData)

      // Update sync status to success
      await updateBolSyncStatus(bolData.bolId, 'success', null, (bolData.sanddrive_sync_attempts || 0) + 1)
      setRetryStatus('success')

      // Refresh the table
      getBOLs({ ...sqlParams, page_number: 0, currentTerminal })

      // Close modal after short delay
      setTimeout(() => {
        handleClose()
      }, 2000)
    } catch (error) {
      console.error('Retry failed:', error)
      setRetryError(error.message || 'Failed to sync with SandDrive')
      setRetryStatus('failed')

      // Update sync status with new error
      await updateBolSyncStatus(
        bolData.bolId,
        'failed',
        error.message || 'Failed to sync with SandDrive',
        (bolData.sanddrive_sync_attempts || 0) + 1
      )
    } finally {
      setIsRetrying(false)
    }
  }

  const handleAcknowledge = async () => {
    await updateBolSyncStatus(
      bolData.bolId,
      'acknowledged',
      bolData.sanddrive_sync_error,
      bolData.sanddrive_sync_attempts
    )
    getBOLs({ ...sqlParams, page_number: 0, currentTerminal })
    handleClose()
  }

  if (!bolData || !invInterData) return null

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Warning style={{ fill: '#ff9800', marginRight: 8 }} />
          SandDrive Sync Failed - BOL #{bolData.bolId}
        </Box>
      </DialogTitle>
      <DialogContent>
        {bolData.sanddrive_sync_error && (
          <Alert severity="warning" style={{ marginBottom: 16 }}>
            <strong>Previous Error:</strong> {bolData.sanddrive_sync_error}
            <br />
            <strong>Attempts:</strong> {bolData.sanddrive_sync_attempts || 0}
          </Alert>
        )}

        <Typography variant="body2" gutterBottom>
          The following BOL will be sent to SandDrive:
        </Typography>

        <Box
          style={{
            border: '1px solid #ccc',
            borderRadius: 4,
            overflow: 'auto',
            maxHeight: 400,
            marginTop: 16,
            marginBottom: 16
          }}
        >
          <BolPreviewForRetry bolData={bolData} invInter={invInterData} />
        </Box>

        {retryStatus === 'success' && (
          <Alert severity="success" icon={<CheckCircle />}>
            Successfully synced with SandDrive!
          </Alert>
        )}

        {retryStatus === 'failed' && <Alert severity="error">Retry failed: {retryError}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleAcknowledge} color="default" startIcon={<Clear />} disabled={isRetrying}>
          Acknowledge & Clear
        </Button>
        <Button onClick={handleClose} disabled={isRetrying}>
          Cancel
        </Button>
        <Button
          onClick={handleRetry}
          color="primary"
          variant="contained"
          disabled={isRetrying || retryStatus === 'success'}
          startIcon={isRetrying ? <CircularProgress size={20} /> : null}
        >
          {isRetrying ? 'Retrying...' : 'Retry Sync'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default BOLSyncRetryModal
